import type { Product } from "./content/products";
import { priceForProduct } from "./pricing";
import type { Currency } from "./currency";
import type { Rates } from "./fx";
import type { ProductCardData } from "@/components/brand/ProductCard";

// Display code for a product, e.g. "BP · 23"
export function displayCode(p: Product): string {
  const tail = p.sku.length >= 2 ? p.sku.slice(-2) : p.sku;
  return `BP · ${tail}`;
}

/* Only return the image when it's a real product photograph (not the
   legacy vector SVG silhouettes that linger in the bundled JSON for
   1023/1082). Anything served from the WP media library or the new
   shoot-f directory counts as real. */
export function isRealPhoto(src: string): boolean {
  if (!src) return false;
  if (src.endsWith(".svg")) return false;
  if (src.includes("/shoot-f/")) return true;
  if (src.startsWith("https://")) return true;        // remote (WP media library)
  if (src.startsWith("http://"))  return true;
  return false;
}

export function realPhoto(p: Product | undefined): string | undefined {
  if (!p?.image) return undefined;
  return isRealPhoto(p.image) ? p.image : undefined;
}

export function realPhotos(p: Product | undefined): string[] {
  if (!p?.images) return [];
  return p.images.filter(isRealPhoto);
}

export interface CardI18n {
  /** Map of canonical English target → localized label, e.g. { "Redness": "Kızarıklık" }. */
  targets?: Record<string, string>;
  /** Localized "+ ADD" button copy. */
  addLabel?: string;
}

export function shortDescriptor(p: Product, targetsMap?: Record<string, string>): string {
  if (p.targets && p.targets.length > 0) {
    return p.targets
      .slice(0, 2)
      .map((t) => targetsMap?.[t] ?? t)
      .join(" · ");
  }
  return p.stepName ?? p.category ?? "";
}

export function toCardData(
  p: Product,
  locale: string,
  currency: Currency,
  rates: Rates,
  i18n: CardI18n = {},
  tag?: string
): ProductCardData {
  const price = priceForProduct(p, currency, rates);
  return {
    code: displayCode(p),
    sku: p.sku,
    name: p.name,
    subtitle: shortDescriptor(p, i18n.targets),
    price: price.amount,
    priceDisplay: price.formatted,
    priceEUR: p.priceEUR ?? 0,
    shortName: p.shortName ?? p.name,
    href: `/${locale}/products/${p.category}/${p.sku}`,
    tag,
    image: realPhoto(p),
    volume: p.volume,
    addLabel: i18n.addLabel ?? "+ ADD",
  };
}

export const STEP_LABELS: Record<number, string> = {
  1: "Cleanse",
  2: "Tone & Balance",
  3: "Focus & Treat",
  4: "Boost & Hydrate",
  5: "Protect",
};
